<template>
    <div class="common-birthdays-menu">
        <v-menu
            v-model="menu_open"
            :close-on-content-click="false"
            bottom
            offset-y
            max-width="600px"
            max-height="500px"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                    :disabled="disabled"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>

            <v-list>
                <v-list-item @click="showCommonBirthdays">
                    <v-list-item-title>Add Common Birthdays</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Common Birthdays Dialog -->
        <v-dialog
            v-model="show_dialog"
            max-width="700px"
        >
            <v-card>
                <v-card-title class="text-h6">
                    Common Birthdays in Your CRM
                    <v-spacer></v-spacer>
                    <v-btn icon @click="show_dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <p class="text-body-2 mb-3">
                        These are the 10 most common birthdays in your organization's CRM data (when excluding our automatically filtered dates).
                        Dates that appear overly frequently in your CRM may be placeholders or test data.
                    </p>

                    <div v-if="loading_common_birthdays" class="text-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-2">Loading common birthdays...</p>
                    </div>

                    <div v-else-if="common_birthdays.length === 0">
                        <v-alert
                            type="info"
                            dense
                            text
                        >
                            No common birthdays found. You don't have multiple customers with the same birthday date.
                        </v-alert>
                    </div>

                    <div v-else>
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Count</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="birthday in common_birthdays" :key="birthday.formatted_date">
                                        <td>{{ birthday.formatted_date }}</td>
                                        <td>{{ birthday.count }}</td>
                                        <td>
                                            <v-btn
                                                x-small
                                                color="primary"
                                                @click="addBirthdayToIgnored(birthday)"
                                                :disabled="isDateAlreadyIgnored(birthday.formatted_date)"
                                            >
                                                {{ isDateAlreadyIgnored(birthday.formatted_date) ? 'Added' : 'Add' }}
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <div class="mt-3">
                            <v-btn
                                color="primary"
                                @click="addAllBirthdaysToIgnored"
                                :disabled="allCommonBirthdaysAlreadyIgnored"
                                block
                                small
                            >
                                Add All to Ignored List
                            </v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
const Time = require('@/js/time.js').default;

export default {
    name: 'MCommonBirthdaysDropdown',

    props: {
        organizationId: {
            type: String,
            required: true
        },
        displayIgnoredBirthDates: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            menu_open: false,
            show_dialog: false,
            common_birthdays: [],
            loading_common_birthdays: false
        }
    },

    computed: {
        allCommonBirthdaysAlreadyIgnored() {
            if (!this.common_birthdays || this.common_birthdays.length === 0) {
                return true;
            }

            return this.common_birthdays.every(birthday =>
                this.isDateAlreadyIgnored(birthday.formatted_date)
            );
        }
    },

    methods: {
        showCommonBirthdays() {
            this.menu_open = false;
            this.show_dialog = true;

            // Automatically load common birthdays when dialog opens
            if (this.common_birthdays.length === 0 && !this.loading_common_birthdays) {
                this.loadCommonBirthdays();
            }
        },

        loadCommonBirthdays() {
            this.loading_common_birthdays = true;

            this.$rc.makeApiCall({
                'url': `organization-data-cleaning-rules/common-birthdays?organization_id=${this.organizationId}`,
                'method': 'get',
                'on_response': 'onResponseStandard',
                'on_response_params': {
                    'on_success': (data) => {
                        this.loading_common_birthdays = false;
                        // Format the birthdays to match the ignored birthday control format
                        this.common_birthdays = (data.common_birthdays || []).map(birthday => {
                            // Convert to MM/DD/YYYY format for display and consistency
                            const formatted_date = this.formatBirthdayForDisplay(birthday);
                            return {
                                ...birthday,
                                formatted_date: formatted_date
                            };
                        });
                    },
                    'on_error': () => {
                        this.loading_common_birthdays = false;
                        this.$rc.alert('Error', 'Unable to load common birthdays');
                    }
                }
            });
        },

        formatBirthdayForDisplay(birthday) {
            // Format birthday to MM/DD/YYYY format using the same utility as ignored birthday control
            const year = birthday.year || new Date().getFullYear();
            const month = birthday.month.toString();
            const day = birthday.day.toString();
            const raw_date = `${month}/${day}/${year}`;

            // Use Time.isoToMMDDYYYY to ensure consistent formatting
            // First convert to ISO, then back to display format for consistency
            const iso_date = Time.mmddyyyyToISOFormat(raw_date);
            return Time.isoToMMDDYYYY(iso_date);
        },

        formatBirthdayForStorage(birthday) {
            // Convert to ISO format (YYYY-MM-DD) for storage using the same utility as ignored birthday control
            const year = birthday.year || new Date().getFullYear();
            const month = birthday.month.toString();
            const day = birthday.day.toString();
            const raw_date = `${month}/${day}/${year}`;

            // Use Time.mmddyyyyToISOFormat for consistent conversion
            return Time.mmddyyyyToISOFormat(raw_date);
        },

        addBirthdayToIgnored(birthday) {
            // Convert to ISO format for storage (same as ignored birthday control)
            const iso_date = this.formatBirthdayForStorage(birthday);

            // Emit event to parent to add this date
            this.$emit('add-ignored-date', iso_date);
        },

        addAllBirthdaysToIgnored() {
            const dates_to_add = [];

            for (const birthday of this.common_birthdays) {
                if (!this.isDateAlreadyIgnored(birthday.formatted_date)) {
                    const iso_date = this.formatBirthdayForStorage(birthday);
                    dates_to_add.push(iso_date);
                }
            }

            if (dates_to_add.length > 0) {
                // Emit event to parent to add all these dates
                this.$emit('add-ignored-dates', dates_to_add);
                this.$rc.alert('Success', `Added ${dates_to_add.length} common birthdays to the ignored list.`);
            }
        },

        isDateAlreadyIgnored(formatted_date) {
            if (!formatted_date || !this.displayIgnoredBirthDates) {
                return false;
            }

            // Convert the common birthday date to ISO format using Time utilities
            const common_iso = Time.mmddyyyyToISOFormat(formatted_date);
            if (!common_iso) {
                return false;
            }

            // Check if any ignored date matches when both are converted to ISO format
            return this.displayIgnoredBirthDates.some(ignored_date => {
                const ignored_iso = Time.mmddyyyyToISOFormat(ignored_date);
                return ignored_iso && ignored_iso === common_iso;
            });
        }
    }
}
</script>

<style scoped>
.common-birthdays-menu {
    display: inline-block;
}
</style>
